/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, ElementRef, Inject, Input, OnChanges, OnDestroy, ViewChild} from '@angular/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import * as Highcharts from 'highcharts';

import template from './spark-chart.component.html';
import {formatLocaleDate} from '../locale/locale-date-format';
import {LocaleHelper} from '../../../components/util/locale-helper';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {CHART_IMPORTS} from './chart-imports';

@Component({
  imports: CHART_IMPORTS,
  selector: 'adn-spark-chart',
  template,
  host: {
    class: 'chart'
  }
})
export class SparkChartComponent implements OnChanges, OnDestroy {
  @Input() stats: any[];

  @ViewChild('chartContainer')
  set chartContainer(chartContainer: ElementRef<HTMLElement>) {
    this.chartContainerRef = chartContainer;
    this.renderChart();
  }

  haveStats = false;
  error = false;
  noData: any = null;
  chartHeight = 100;

  private chart: Highcharts.Chart | null = null;
  private chartContainerRef: ElementRef<HTMLElement> | null = null;
  private chartOptions: any = null;

  constructor(
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(LocalUserProfile) private readonly localUserProfile: any
  ) {
  }

  ngOnChanges(): void {
    this.updateChartState();
  }

  ngOnDestroy(): void {
    this.destroyChart();
  }

  private formatChartDate(value: any): string {
    const locale = LocaleHelper.toAngularFormat(this.localUserProfile.get('locale') || 'en_GB');
    return formatLocaleDate(value, 'medium', locale, this.LocalNetworkProfile.get('timeZone') || 'utc') || '';
  }

  private updateChartState(): void {
    if (!this.stats) {
      this.haveStats = false;
      this.chartOptions = null;
      this.destroyChart();
      return;
    }

    const series: any[] = [{
      name: 'Upper CPM',
      id: 'upperCpm',
      type: 'line'
    }, {
      name: 'Lower CPM',
      id: 'lowerCpm',
      type: 'line'
    }];

    const eitherPass: number[] = [];
    _.forEach(series, (s) => {
      let prev = 0;
      let prevTime: any = 0;
      s.data = _.map(this.stats, (stat, index) => {
        const currentValue = Math.round(stat[s.id].amount * 10000) / 10000;
        const currentTime = stat.time;
        const passFilter = Math.abs(prev - currentValue) > 0.5 && (prevTime === 0 || moment(prevTime).diff(moment(currentTime), 'minutes') > 30);
        const returnValue = {
          x: moment(currentTime).valueOf(),
          y: currentValue,
          passFilter
        };
        if (passFilter) {
          eitherPass.push(Number(index));
          prev = currentValue;
          prevTime = currentTime;
        }
        return returnValue;
      });
    });
    _.forEach(series, (s) => {
      s.data = _.filter(s.data, (_stat, index: number) => {
        return eitherPass.indexOf(index) > -1;
      });
    });

    this.chartOptions = this.createChartOptions(series);
    this.haveStats = true;
    this.renderChart();
  }

  private createChartOptions(series: any[]): any {
    const formatChartDate = this.formatChartDate.bind(this);
    return {
      chart: {
        type: 'line',
        zoomType: 'x',
        showAxes: true,
        height: this.chartHeight
      },
      plotOptions: {
        series: {
          marker: {
            enabled: true
          },
          turboThreshold: 2000
        }
      },
      tooltip: {
        shared: true,
        formatter: function() {
          return this.points.reduce(function(s, point) {
            return s + "<br><span style='color:" + point.color + "'>\u25CF</span> " + point.series.name + ": " + point.y;
          }, "<b>" + formatChartDate(this.x) + "</b>");
        }
      },
      yAxis: {
        title: {text: null},
        min: 0,
        allowDecimals: false,
        id: '0'
      },
      xAxis: {
        type: 'datetime',
        title: {
          enabled: false,
          text: 'Date and Time'
        }
      },
      legend: {
        enabled: false
      },
      credits: {
        enabled: false
      },
      accessibility: {
        enabled: false
      },
      title: {
        text: null
      },
      series
    };
  }

  private renderChart(): void {
    if (!this.chartContainerRef?.nativeElement || !this.chartOptions || this.noData) {
      return;
    }

    this.destroyChart();
    this.chart = Highcharts.chart(this.chartContainerRef.nativeElement, this.chartOptions);
  }

  private destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }
}
